/**
 * Client API per piattaforma collaborativa
 * Tutti i dati sono salvati nel backend (database SQLite).
 */

import { ensurePlannerSlotName } from './utils.js';

const API_BASE = '';
const AUTH_TOKEN_KEY = 'turni_token';
const AUTH_USER_KEY = 'turni_user';

/**
 * Dove vive la sessione: localStorage se al login e' stato scelto "Ricorda questo
 * dispositivo" (sopravvive alla chiusura dell'app), altrimenti sessionStorage, che iOS
 * azzera quando termina la web app. Mai in entrambi: setAuth svuota l'altro.
 * Ogni accesso e' protetto: con lo storage bloccato (es. navigazione privata) il getter
 * stesso puo' lanciare un'eccezione.
 */
function authStores() {
  const stores = [];
  try { stores.push(sessionStorage); } catch (_) {}
  try { stores.push(localStorage); } catch (_) {}
  return stores;
}

function authStoreHolding(key) {
  for (const store of authStores()) {
    try {
      if (store.getItem(key)) return store;
    } catch (_) {}
  }
  return null;
}

export function getAuthToken() {
  const store = authStoreHolding(AUTH_TOKEN_KEY);
  try {
    return store ? store.getItem(AUTH_TOKEN_KEY) : null;
  } catch (_) {
    return null;
  }
}

/** Salva la sessione appena aperta. `remember` = "Ricorda questo dispositivo". */
export function setAuth(token, user, remember = false) {
  clearAuth();
  const write = (store) => {
    store.setItem(AUTH_TOKEN_KEY, token);
    store.setItem(AUTH_USER_KEY, JSON.stringify(user));
  };
  if (remember) {
    try {
      write(localStorage);
      return;
    } catch (_) {
      // localStorage non disponibile: si ripiega sulla sessione corrente.
    }
  }
  write(sessionStorage);
}

/** Aggiorna l'utente salvato nello stesso storage in cui si trova la sessione. */
export function updateStoredUser(updater) {
  const store = authStoreHolding(AUTH_USER_KEY);
  if (!store) return;
  try {
    const current = JSON.parse(store.getItem(AUTH_USER_KEY));
    const next = updater(current) || current;
    store.setItem(AUTH_USER_KEY, JSON.stringify(next));
  } catch (_) {}
}

function normalizeUserIdentity(user) {
  if (!user || typeof user !== 'object') return null;
  const id = user.id ?? user.userId ?? null;
  const doctorId = user.doctorId ?? user.doctor_id ?? null;
  return {
    ...user,
    id,
    userId: id,
    doctorId,
    doctor_id: doctorId,
  };
}

function getAuthHeaders() {
  const token = getAuthToken();
  const h = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

async function fetchApi(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body.detail) detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    } catch (_) {}
    if (res.status === 502 && !detail) detail = 'Backend non raggiungibile. Verifica che il server sia avviato (./start_server.sh).';
    throw new Error(`API ${res.status}: ${detail}`);
  }
  if (res.status === 204 || res.headers.get('content-length') === '0') return null;
  return res.json();
}

export async function checkApiAvailable() {
  try {
    const r = await fetch(`${API_BASE}/api/status`, { method: 'GET' });
    return r.ok;
  } catch {
    return false;
  }
}

export async function getApiStatus() {
  const r = await fetch(`${API_BASE}/api/status`, { method: 'GET' });
  if (!r.ok) return null;
  return r.json();
}

/** Login con username e password. Ritorna { token, user } o solleva. */
export async function login(username, password, remember = false) {
  const r = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username: (username || '').trim(), password: password || '', remember: Boolean(remember) }),
  });
  if (r.status === 401) return null;
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    const msg = body.detail || (r.status === 404 ? 'Endpoint non trovato. Riavvia il backend (./start_server.sh).' : `Auth ${r.status}`);
    throw new Error(msg);
  }
  const data = await r.json();
  return { token: data.token, user: normalizeUserIdentity(data.user) };
}

export function getStoredUser() {
  try {
    const store = authStoreHolding(AUTH_USER_KEY);
    if (!store) return null;
    const parsed = JSON.parse(store.getItem(AUTH_USER_KEY));
    const normalized = normalizeUserIdentity(parsed);
    if (normalized && JSON.stringify(parsed) !== JSON.stringify(normalized)) {
      store.setItem(AUTH_USER_KEY, JSON.stringify(normalized));
    }
    return normalized;
  } catch {
    return null;
  }
}

export function clearAuth() {
  for (const store of authStores()) {
    try {
      store.removeItem(AUTH_TOKEN_KEY);
      store.removeItem(AUTH_USER_KEY);
      store.removeItem('turni_auth'); // legacy
    } catch (_) {}
  }
}

/**
 * Revoca la sessione sul server prima di cancellarla dal dispositivo: con un dispositivo
 * ricordato il token restava altrimenti valido fino alla scadenza. Best effort e al massimo
 * 3 secondi: il logout non deve mai restare appeso alla rete.
 */
export async function apiLogout() {
  const token = getAuthToken();
  if (!token) return;
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), 3000) : null;
  try {
    await fetch(`${API_BASE}/api/auth/logout`, { method: 'POST', headers: getAuthHeaders(), signal: ctrl?.signal });
  } catch (_) {
    // offline o server irraggiungibile: si esce comunque dal dispositivo.
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function getFullState() {
  return fetchApi('/api/state');
}

// Users (admin only)
export async function apiGetUsers() {
  return fetchApi('/api/users');
}

export async function apiCreateUser(user) {
  const r = await fetchApi('/api/users', {
    method: 'POST',
    body: JSON.stringify({
      username: user.username,
      password: user.password,
      role: user.role,
      doctorId: user.doctorId || null,
      email: user.email?.trim() || null,
      emails: Array.isArray(user.emails) ? user.emails : undefined,
    }),
  });
  return r.id;
}

export async function apiUpdateUser(id, updates) {
  await fetchApi(`/api/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function apiDeleteUser(id) {
  await fetchApi(`/api/users/${id}`, { method: 'DELETE' });
}

/** Cambio password dell'utente corrente. Solleva se password attuale errata o nuova invalida. */
export async function apiChangeMyPassword(currentPassword, newPassword) {
  await fetchApi('/api/users/me/password', {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

/** Profilo utente corrente (include email). */
export async function apiGetMe() {
  return fetchApi('/api/users/me');
}

/** Aggiorna profilo (es. email). */
export async function apiPatchMe(updates) {
  return fetchApi('/api/users/me', {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Richiesta recupero password (senza token Bearer).
 * @param {string} identifier - username oppure email
 */
export async function apiForgotPassword(identifier) {
  const id = (identifier || '').trim();
  if (!id) throw new Error('Inserisci username o email');
  const body = id.includes('@') ? { email: id } : { username: id };
  const r = await fetch(`${API_BASE}/api/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const detail = data.detail || (typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail || {}));
    throw new Error(detail || `Errore ${r.status}`);
  }
  return data;
}

/** Imposta nuova password con token da link email (senza Bearer). */
export async function apiResetPassword(token, newPassword) {
  const r = await fetch(`${API_BASE}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, newPassword }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const detail = data.detail || (typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail || {}));
    throw new Error(detail || `Errore ${r.status}`);
  }
  return data;
}

// Admin: Backup / Panic / Ripristino
export async function apiCreateBackup(label = '') {
  const r = await fetchApi('/api/admin/backup', {
    method: 'POST',
    body: JSON.stringify({ label: label || '' }),
  });
  return r;
}

export async function apiListBackups() {
  return fetchApi('/api/admin/backups');
}

export async function apiRestoreBackup(backupId, confirmText) {
  await fetchApi(`/api/admin/restore/${encodeURIComponent(backupId)}`, {
    method: 'POST',
    body: JSON.stringify({ confirmText: confirmText || '' }),
  });
}

export async function apiDeleteBackup(backupId) {
  await fetchApi(`/api/admin/backups/${encodeURIComponent(backupId)}`, { method: 'DELETE' });
}

/**
 * Solo beta/alfa: copia DB upstream sul DB operativo (admin + step-up + conferma SINCRONIZZA).
 * @param {'live'|'beta'} [source] — default live; beta solo su canale alfa.
 */
export async function apiSyncFromLive(confirmText, source = 'live') {
  return fetchApi('/api/admin/sync-from-live', {
    method: 'POST',
    body: JSON.stringify({ confirmText: confirmText || '', source: source || 'live' }),
  });
}

/**
 * Solo beta/alfa: copia dall'upstream le sole assenze/desiderata/indisponibilità,
 * senza sostituire il DB (admin + step-up + conferma SINCRONIZZA).
 * @param {'live'|'beta'} [source] — default live; beta solo su canale alfa.
 */
export async function apiSyncAbsencesFromUpstream(confirmText, source = 'live') {
  return fetchApi('/api/admin/sync-absences-from-upstream', {
    method: 'POST',
    body: JSON.stringify({ confirmText: confirmText || '', source: source || 'live' }),
  });
}

export async function apiGetShiftSlotsConsistencyDiagnostics() {
  return fetchApi('/api/admin/shift-slots/consistency-diagnostics');
}

/** Admin: elenco duplicati stessa cella logica (data, fascia, categoria, UOC, tipo, nome; include nome vuoto). */
export async function apiGetShiftSlotsHygieneSameNameDuplicates() {
  return fetchApi('/api/admin/shift-slots/hygiene-same-name-duplicates');
}

/** Admin + step-up: allinea UOC da categoria su slot + calendario; opz. riconcilia duplicati logici. */
export async function apiPostShiftSlotsHygieneAlignUocs(body = {}) {
  return fetchApi('/api/admin/shift-slots/hygiene-align-uocs', {
    method: 'POST',
    body: JSON.stringify({
      alsoReconcileLogicalDuplicates: body.alsoReconcileLogicalDuplicates !== false,
    }),
  });
}

/** Admin + step-up: unifica slot duplicati (stessa chiave logica Piano turni). */
export async function apiPostShiftSlotsHygieneDedupeSameName() {
  return fetchApi('/api/admin/shift-slots/hygiene-dedupe-same-name', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Invia il PDF del piano turni via email.
 * Con recipientEmail invia solo a quell'indirizzo; con recipientUserId a tutti i
 * recapiti di quell'utente abilitati al piano turni; senza nessuno dei due, a tutti.
 */
export async function apiSendShiftPlanPdfEmail({
  dateFrom,
  dateTo,
  pdfBlob,
  filename,
  recipientEmail = null,
  recipientUserId = null,
  icsBlob = null,
  icsFilename = null,
  subject = null,
  messageText = null,
}) {
  if (!dateFrom || !dateTo) throw new Error('Intervallo date mancante');
  if (!pdfBlob) throw new Error('Allegato PDF mancante');
  const token = getAuthToken();
  const formData = new FormData();
  formData.append('dateFrom', dateFrom);
  formData.append('dateTo', dateTo);
  formData.append('pdfFile', pdfBlob, filename || 'Piano_Turni.pdf');
  if (recipientEmail) formData.append('recipientEmail', String(recipientEmail).trim());
  if (recipientUserId) formData.append('recipientUserId', String(recipientUserId).trim());
  if (icsBlob) formData.append('icsFile', icsBlob, icsFilename || `turni-${dateFrom}-${dateTo}.ics`);
  if (subject) formData.append('subject', String(subject));
  if (messageText) formData.append('messageText', String(messageText));
  const r = await fetch(`${API_BASE}/api/admin/shift-plan/email`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const detail = data?.detail;
    const msg = typeof detail === 'string'
      ? detail
      : detail?.message || JSON.stringify(detail || data || {});
    throw new Error(msg || `Errore ${r.status}`);
  }
  return data;
}

export async function apiExportShiftPlanXlsx(
  rows,
  {
    title = 'Piano turni',
    rangeLabel = '',
    filename = 'Piano_Turni.xlsx',
    doctors = [],
    absenceRows = [],
  } = {}
) {
  const token = getAuthToken();
  const r = await fetch(`${API_BASE}/api/shift-plan/export-xlsx`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ rows: rows || [], doctors: doctors || [], absenceRows: absenceRows || [], title, rangeLabel, filename }),
  });
  if (!r.ok) {
    let detail = r.statusText || `Errore ${r.status}`;
    const text = await r.text().catch(() => '');
    try {
      const data = text ? JSON.parse(text) : null;
      if (data?.detail) detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
      else if (text) detail = text;
    } catch (_) {
      if (text) detail = text;
    }
    throw new Error(detail);
  }
  const blob = await r.blob();
  const contentDisposition = r.headers.get('content-disposition') || '';
  const m = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
  return { blob, filename: m?.[1] || filename };
}

/** Importa il piano turni da un file Excel (template o tabellone) oppure PDF. */
export async function apiImportShiftPlanXlsx(file) {
  if (!file) throw new Error('File mancante');
  const token = getAuthToken();
  const form = new FormData();
  form.append('file', file);
  const r = await fetch(`${API_BASE}/api/shift-plan/import-xlsx`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const detail = data?.detail;
    const msg = typeof detail === 'string' ? detail : detail?.message || JSON.stringify(detail || data || {});
    throw new Error(msg || `Errore ${r.status}`);
  }
  return data;
}

/** Scarica calendario personale turni in formato ICS. */
export async function apiDownloadMyShiftCalendarIcs(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) throw new Error('Intervallo date mancante');
  const token = getAuthToken();
  const qs = new URLSearchParams({ dateFrom, dateTo });
  const r = await fetch(`${API_BASE}/api/me/shift-calendar.ics?${qs.toString()}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) {
    let detail = r.statusText || `Errore ${r.status}`;
    const text = await r.text().catch(() => '');
    try {
      const json = text ? JSON.parse(text) : null;
      if (json?.detail) detail = typeof json.detail === 'string' ? json.detail : JSON.stringify(json.detail);
      else if (text) detail = text;
    } catch (_) {
      if (text) detail = text;
    }
    throw new Error(detail);
  }
  const blob = await r.blob();
  const contentDisposition = r.headers.get('content-disposition') || '';
  const m = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
  return {
    blob,
    filename: m?.[1] || `turni-${dateFrom}-${dateTo}.ics`,
  };
}

/** Scarica calendario ICS del medico specificato (admin/director). */
export async function apiDownloadShiftCalendarIcsForDoctor(doctorId, dateFrom, dateTo) {
  if (!doctorId) throw new Error('Medico mancante');
  if (!dateFrom || !dateTo) throw new Error('Intervallo date mancante');
  const token = getAuthToken();
  const qs = new URLSearchParams({ doctorId, dateFrom, dateTo });
  const r = await fetch(`${API_BASE}/api/admin/shift-calendar.ics?${qs.toString()}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) {
    let detail = r.statusText || `Errore ${r.status}`;
    const text = await r.text().catch(() => '');
    try {
      const json = text ? JSON.parse(text) : null;
      if (json?.detail) detail = typeof json.detail === 'string' ? json.detail : JSON.stringify(json.detail);
      else if (text) detail = text;
    } catch (_) {
      if (text) detail = text;
    }
    throw new Error(detail);
  }
  const blob = await r.blob();
  const contentDisposition = r.headers.get('content-disposition') || '';
  const m = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
  return {
    blob,
    filename: m?.[1] || `turni-${doctorId}-${dateFrom}-${dateTo}.ics`,
  };
}

/** Scarica calendario ICS delta del medico (admin/director). */
export async function apiDownloadShiftCalendarDeltaIcsForDoctor({ doctorId, dateFrom, dateTo, addedSlotIds = [], removedSlotIds = [] }) {
  if (!doctorId) throw new Error('Medico mancante');
  if (!dateFrom || !dateTo) throw new Error('Intervallo date mancante');
  const token = getAuthToken();
  const r = await fetch(`${API_BASE}/api/admin/shift-calendar-delta.ics`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ doctorId, dateFrom, dateTo, addedSlotIds, removedSlotIds }),
  });
  if (!r.ok) {
    let detail = r.statusText || `Errore ${r.status}`;
    const text = await r.text().catch(() => '');
    try {
      const json = text ? JSON.parse(text) : null;
      if (json?.detail) detail = typeof json.detail === 'string' ? json.detail : JSON.stringify(json.detail);
      else if (text) detail = text;
    } catch (_) {
      if (text) detail = text;
    }
    throw new Error(detail);
  }
  const blob = await r.blob();
  const contentDisposition = r.headers.get('content-disposition') || '';
  const m = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
  return {
    blob,
    filename: m?.[1] || `turni-delta-${doctorId}-${dateFrom}-${dateTo}.ics`,
  };
}

/** Admin/director: invia Web Push a utenti specifici. */
export async function apiAdminPushNotify({ userIds, title, body, url }) {
  return fetchApi('/api/admin/push/notify', {
    method: 'POST',
    body: JSON.stringify({ userIds: userIds || [], title: title || null, body: body || null, url: url || null }),
  });
}

/** Admin/director: notifica in-app + push «turni aggiornati» dopo delta piano (post-mail). */
export async function apiAdminShiftPlanNotifyDelta({ userIds, dateFrom, dateTo }) {
  return fetchApi('/api/admin/shift-plan-notify-delta', {
    method: 'POST',
    body: JSON.stringify({
      userIds: userIds || [],
      dateFrom: String(dateFrom || ''),
      dateTo: String(dateTo || ''),
    }),
  });
}

export async function apiListNotifications({ limit = 40, includeDismissed = false, cursor = null } = {}) {
  const qs = new URLSearchParams();
  qs.set('limit', String(limit));
  if (includeDismissed) qs.set('includeDismissed', 'true');
  if (cursor?.createdAt) qs.set('cursorCreatedAt', cursor.createdAt);
  if (cursor?.id) qs.set('cursorId', cursor.id);
  return fetchApi(`/api/notifications?${qs.toString()}`);
}

export async function apiNotificationsUnreadCount() {
  return fetchApi('/api/notifications/unread-count');
}

export async function apiMarkNotificationRead(id) {
  return fetchApi(`/api/notifications/${encodeURIComponent(id)}/read`, { method: 'PATCH' });
}

export async function apiDismissNotification(id) {
  return fetchApi(`/api/notifications/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// Security (admin)
export async function apiGetSecurityEvents(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.userId) qs.set('userId', filters.userId);
  if (filters.username) qs.set('username', filters.username);
  if (filters.riskOnly) qs.set('riskOnly', 'true');
  if (filters.limit) qs.set('limit', String(filters.limit));
  const q = qs.toString();
  return fetchApi(`/api/admin/security/events${q ? `?${q}` : ''}`);
}

export async function apiGetSecurityEventsPage(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.userId) qs.set('userId', filters.userId);
  if (filters.username) qs.set('username', filters.username);
  if (filters.riskOnly) qs.set('riskOnly', 'true');
  if (filters.eventType) qs.set('eventType', filters.eventType);
  if (typeof filters.success === 'boolean') qs.set('success', String(filters.success));
  if (filters.limit) qs.set('limit', String(filters.limit));
  if (filters.cursorCreatedAt) qs.set('cursorCreatedAt', filters.cursorCreatedAt);
  if (filters.cursorId) qs.set('cursorId', filters.cursorId);
  if (filters.fromCreatedAt) qs.set('fromCreatedAt', filters.fromCreatedAt);
  if (filters.toCreatedAt) qs.set('toCreatedAt', filters.toCreatedAt);
  return fetchApi(`/api/admin/security/events-page?${qs.toString()}`);
}

export async function apiGetSecuritySummary(userId, riskOnly = false) {
  const qs = new URLSearchParams();
  qs.set('userId', userId);
  if (riskOnly) qs.set('riskOnly', 'true');
  return fetchApi(`/api/admin/security/summary?${qs.toString()}`);
}

/** Ultimo accesso (login OK) per ogni utente registrato. */
export async function apiGetSecurityUsersLastAccess() {
  return fetchApi('/api/admin/security/users-last-access');
}

export async function apiGetSecurityDevices(userId, limit = 50) {
  const qs = new URLSearchParams();
  qs.set('userId', userId);
  qs.set('limit', String(limit));
  return fetchApi(`/api/admin/security/devices?${qs.toString()}`);
}

export async function apiGetSecurityDevicesPage(filters = {}) {
  const qs = new URLSearchParams();
  qs.set('userId', filters.userId || '');
  if (filters.limit) qs.set('limit', String(filters.limit));
  if (filters.cursorLastSeenAt) qs.set('cursorLastSeenAt', filters.cursorLastSeenAt);
  if (filters.cursorDeviceHash) qs.set('cursorDeviceHash', filters.cursorDeviceHash);
  return fetchApi(`/api/admin/security/devices-page?${qs.toString()}`);
}

export async function apiGetSecurityAlerts(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.limit) qs.set('limit', String(filters.limit));
  const q = qs.toString();
  return fetchApi(`/api/admin/security/alerts${q ? `?${q}` : ''}`);
}

export async function apiGetSecurityAlertsPage(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.limit) qs.set('limit', String(filters.limit));
  if (filters.cursorCreatedAt) qs.set('cursorCreatedAt', filters.cursorCreatedAt);
  if (filters.cursorId) qs.set('cursorId', filters.cursorId);
  return fetchApi(`/api/admin/security/alerts-page?${qs.toString()}`);
}

export async function apiAckSecurityAlert(id, status = 'ack') {
  await fetchApi(`/api/admin/security/alerts/${encodeURIComponent(id)}/ack`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

export async function apiGetSecuritySessions(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.userId) qs.set('userId', filters.userId);
  if (filters.activeOnly) qs.set('activeOnly', 'true');
  if (filters.limit) qs.set('limit', String(filters.limit));
  const q = qs.toString();
  return fetchApi(`/api/admin/security/sessions${q ? `?${q}` : ''}`);
}

export async function apiGetSecuritySessionsPage(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.userId) qs.set('userId', filters.userId);
  if (filters.activeOnly) qs.set('activeOnly', 'true');
  if (filters.limit) qs.set('limit', String(filters.limit));
  if (filters.cursorLastSeenAt) qs.set('cursorLastSeenAt', filters.cursorLastSeenAt);
  if (filters.cursorJti) qs.set('cursorJti', filters.cursorJti);
  return fetchApi(`/api/admin/security/sessions-page?${qs.toString()}`);
}

export async function apiRevokeSecuritySession(jti, reason = '') {
  await fetchApi(`/api/admin/security/sessions/${encodeURIComponent(jti)}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function apiStepUp(currentPassword) {
  return fetchApi('/api/auth/step-up', {
    method: 'POST',
    body: JSON.stringify({ currentPassword: currentPassword || '' }),
  });
}

// Doctors
export async function apiGetDoctors() {
  return fetchApi('/api/doctors');
}

export async function apiCreateDoctor(doctor) {
  const r = await fetchApi('/api/doctors', {
    method: 'POST',
    body: JSON.stringify(doctor),
  });
  return r.id;
}

export async function apiUpdateDoctor(id, doctor) {
  await fetchApi(`/api/doctors/${id}`, {
    method: 'PUT',
    body: JSON.stringify(doctor),
  });
}

export async function apiDeleteDoctor(id) {
  await fetchApi(`/api/doctors/${id}`, { method: 'DELETE' });
}

// Absences
export async function apiGetAbsences() {
  return fetchApi('/api/absences');
}

export async function apiCreateAbsence(absence) {
  const r = await fetchApi('/api/absences', {
    method: 'POST',
    body: JSON.stringify({
      doctorId: absence.doctorId,
      dateStart: absence.dateStart,
      dateEnd: absence.dateEnd,
      category: absence.category,
      slotTypes: absence.slotTypes || [],
      oreFormazione: absence.oreFormazione ?? 0,
    }),
  });
  return r.id;
}

export async function apiDeleteAbsence(id) {
  await fetchApi(`/api/absences/${id}`, { method: 'DELETE' });
}

// Recurring exclusions
export async function apiGetRecurringExclusions() {
  return fetchApi('/api/recurring-exclusions');
}

export async function apiGetRecurringOverrides() {
  return fetchApi('/api/recurring-exclusions/overrides');
}

export async function apiCreateRecurringExclusion(r) {
  const res = await fetchApi('/api/recurring-exclusions', {
    method: 'POST',
    body: JSON.stringify({
      doctorId: r.doctorId,
      category: r.category,
      weekday: r.weekday,
      slotTypes: r.slotTypes || [],
      dateFrom: r.dateFrom || null,
      dateTo: r.dateTo || null,
    }),
  });
  return res.id;
}

export async function apiDeleteRecurringExclusion(id) {
  await fetchApi(`/api/recurring-exclusions/${id}`, { method: 'DELETE' });
}

export async function apiAddRecurringOverride(recurringId, date) {
  await fetchApi('/api/recurring-exclusions/overrides', {
    method: 'POST',
    body: JSON.stringify({ recurringId, date }),
  });
}

export async function apiDeleteRecurringOverride(recurringId, date) {
  await fetchApi(`/api/recurring-exclusions/overrides?recurringId=${encodeURIComponent(recurringId)}&date=${encodeURIComponent(date)}`, { method: 'DELETE' });
}

// Desiderata
export async function apiGetDesiderata() {
  return fetchApi('/api/desiderata');
}

export async function apiSetDesiderata(doctorId, date, types) {
  await fetchApi('/api/desiderata', {
    method: 'POST',
    body: JSON.stringify({ doctorId, date, types }),
  });
}

// UOCs
export async function apiGetUocs() {
  return fetchApi('/api/uocs');
}

// Calendar templates
export async function apiGetTemplates() {
  return fetchApi('/api/calendar/templates');
}

/**
 * Allinea a getTemplateWeekdays (types.js): se `weekdays` è stringa JSON o assente,
 * non ridurre mai a un solo giorno per errore (prima: solo Array.isArray → [] → [primary]).
 */
function templateWeekdaysForApiBody(template) {
  let w = template.weekdays;
  if (typeof w === 'string') {
    try {
      w = JSON.parse(w);
      if (typeof w === 'string') {
        try {
          w = JSON.parse(w);
        } catch {
          w = [];
        }
      }
    } catch {
      w = [];
    }
  }
  if (Array.isArray(w) && w.length > 0) {
    return w.filter((x) => typeof x === 'string' && x);
  }
  const fb = template.weekday;
  return fb ? [fb] : [];
}

/** Body template: sempre `weekday` + `weekdays` (JSON.stringify omette undefined → 422 sul backend se manca weekday). */
function calendarTemplateApiBody(template) {
  const ws = templateWeekdaysForApiBody(template);
  const primary = template.weekday || ws[0] || 'lun';
  const weekdays = ws.length ? ws : [primary];
  const at = template.activityType || 'clinico';
  const rawName = template.name == null ? '' : String(template.name);
  return {
    uocId: template.uocId,
    timeSlot: template.timeSlot,
    category: template.category,
    activityType: at,
    name: ensurePlannerSlotName(template.category, rawName, template.timeSlot, at),
    dateFrom: template.dateFrom ?? null,
    dateTo: template.dateTo ?? null,
    weekday: primary,
    weekdays,
  };
}

function calendarModificationApiBody(mod) {
  const at = mod.activityType || 'clinico';
  const cat = String(mod.category || '').trim();
  const isRemove = String(mod.modificationType || '') === 'remove';
  const rawName = mod.name == null ? '' : String(mod.name).trim();
  const name = isRemove && !rawName
    ? ''
    : ensurePlannerSlotName(cat, rawName, mod.timeSlot, at);
  return {
    modificationType: mod.modificationType,
    scope: mod.scope,
    uocId: String(mod.uocId || '').trim() || undefined,
    weekday: mod.weekday,
    dateSpecific: mod.dateSpecific,
    dateFrom: mod.dateFrom,
    dateTo: mod.dateTo,
    timeSlot: mod.timeSlot,
    category: mod.category,
    activityType: at,
    name,
  };
}

export async function apiCreateTemplate(template) {
  const r = await fetchApi('/api/calendar/templates', {
    method: 'POST',
    body: JSON.stringify(calendarTemplateApiBody(template)),
  });
  return r.id;
}

export async function apiUpdateTemplate(id, template) {
  await fetchApi(`/api/calendar/templates/${id}/update`, {
    method: 'POST',
    body: JSON.stringify(calendarTemplateApiBody(template)),
  });
}

export async function apiDeleteTemplate(id) {
  await fetchApi(`/api/calendar/templates/${id}`, { method: 'DELETE' });
}

// Calendar modifications
export async function apiGetModifications() {
  return fetchApi('/api/calendar/modifications');
}

export async function apiCreateModification(mod) {
  const r = await fetchApi('/api/calendar/modifications', {
    method: 'POST',
    body: JSON.stringify(calendarModificationApiBody(mod)),
  });
  return r.id;
}

export async function apiUpdateModification(id, mod) {
  await fetchApi(`/api/calendar/modifications/${id}/update`, {
    method: 'POST',
    body: JSON.stringify(calendarModificationApiBody(mod)),
  });
}

export async function apiDeleteModification(id, opts = {}) {
  // preserveLocked: il backend rifiuta di cancellare la modifica se ne derivano
  // slot con assegnazione lucchettata (usato da "Pulisci bozza").
  const q = opts && opts.preserveLocked ? '?preserveLocked=1' : '';
  await fetchApi(`/api/calendar/modifications/${id}${q}`, { method: 'DELETE' });
}

// Shift slots
export async function apiGetShiftSlots(month) {
  const q = month ? `?month=${month}` : '';
  return fetchApi(`/api/shift-slots${q}`);
}

export async function apiCreateShiftSlot(slot) {
  const activityType = slot.activityType || slot.activity_type || 'clinico';
  const name = ensurePlannerSlotName(slot.category, slot.name, slot.timeSlot || slot.time_slot, activityType);
  const r = await fetchApi('/api/shift-slots', {
    method: 'POST',
    body: JSON.stringify({
      slotKey: slot.slotKey || slot.id,
      name,
      category: slot.category,
      activityType,
      timeSlot: slot.timeSlot || slot.time_slot,
      date: slot.date,
      weekday: slot.weekday,
      uocId: slot.uocId ?? slot.uoc_id,
    }),
  });
  return r.id;
}

export async function apiDeleteShiftSlot(id) {
  await fetchApi(`/api/shift-slots/${id}`, { method: 'DELETE' });
}

// Assignments
export async function apiGetAssignments() {
  return fetchApi('/api/shift-assignments');
}

export async function apiSetAssignment(slotId, doctorId, locked = false, persistOpts = {}) {
  const body = { slotId, doctorId, locked };
  if (persistOpts.allowDirezioneManualOverride) body.allowDirezioneManualOverride = true;
  if (persistOpts.allowNightRestSmontoManualOverride) body.allowNightRestSmontoManualOverride = true;
  await fetchApi('/api/shift-assignments', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function apiDeleteAssignment(slotId) {
  await fetchApi(`/api/shift-assignments/${slotId}`, { method: 'DELETE' });
}

export async function apiSetAssignmentByKey(slotKey, doctorId, locked = false, persistOpts = {}) {
  const body = { slotKey, doctorId, locked };
  if (persistOpts.allowDirezioneManualOverride) body.allowDirezioneManualOverride = true;
  if (persistOpts.allowNightRestSmontoManualOverride) body.allowNightRestSmontoManualOverride = true;
  // Chiave logica stabile: il server risolve la cella anche quando la slot_key
  // persistita è stata riscritta (rebind -tpl/-mod → lgk/-oneoff).
  if (persistOpts.logicalKey) body.logicalKey = persistOpts.logicalKey;
  await fetchApi('/api/shift-assignments-by-key', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function apiDeleteAssignmentByKey(slotKey, logicalKey = '') {
  const qs = logicalKey ? `?logicalKey=${encodeURIComponent(logicalKey)}` : '';
  await fetchApi(`/api/shift-assignments-by-key/${encodeURIComponent(slotKey)}${qs}`, { method: 'DELETE' });
}

// Workload residual (carico residuo medico/settimana)
export async function apiGetWorkloadResidual(weekFrom, weekTo) {
  const params = new URLSearchParams();
  if (weekFrom) params.set('week_from', weekFrom);
  if (weekTo) params.set('week_to', weekTo);
  return fetchApi(`/api/workload-residual?${params}`);
}

export async function apiUpsertWorkloadResidual(rows) {
  return fetchApi('/api/workload-residual', {
    method: 'POST',
    body: JSON.stringify({ rows }),
  });
}

// Workload history (storico carichi per bilanciamento)
export async function apiGetWorkloadHistory() {
  return fetchApi('/api/workload-history');
}

export async function apiSaveWorkloadMonth(monthKey, data) {
  await fetchApi('/api/workload-history', {
    method: 'POST',
    body: JSON.stringify({ monthKey, data }),
  });
}

export async function apiDeleteWorkloadMonth(monthKey) {
  await fetchApi(`/api/workload-history/${encodeURIComponent(monthKey)}`, {
    method: 'DELETE',
  });
}

// Consolidamento bimestre (Circolare ISS n.6/2026)
export async function apiGetBimesterSnapshots() {
  return fetchApi('/api/workload-bimester');
}

export async function apiSaveBimesterSnapshot(bimesterKey, data) {
  await fetchApi('/api/workload-bimester', {
    method: 'POST',
    body: JSON.stringify({ bimesterKey, data }),
  });
}

export async function apiDeleteBimesterSnapshot(bimesterKey) {
  await fetchApi(`/api/workload-bimester/${encodeURIComponent(bimesterKey)}`, {
    method: 'DELETE',
  });
}

// Locked weeks (settimane archiviate)
export async function apiGetLockedWeeks() {
  return fetchApi('/api/locked-weeks');
}

export async function apiLockWeek(weekStart) {
  return fetchApi('/api/locked-weeks', {
    method: 'POST',
    body: JSON.stringify({ weekStart }),
  });
}

export async function apiUnlockWeek(weekStart) {
  await fetchApi(`/api/locked-weeks/${encodeURIComponent(weekStart)}`, { method: 'DELETE' });
}

// Blocco richieste assenze/desiderata (solo effetto su utenti medico)
export async function apiGetAbsenceRequestLockedWeeks() {
  return fetchApi('/api/absence-request-locked-weeks');
}

export async function apiLockAbsenceRequestWeek(weekStart) {
  await fetchApi('/api/absence-request-locked-weeks', {
    method: 'POST',
    body: JSON.stringify({ weekStart }),
  });
}

export async function apiUnlockAbsenceRequestWeek(weekStart) {
  await fetchApi(`/api/absence-request-locked-weeks/${encodeURIComponent(weekStart)}`, { method: 'DELETE' });
}

// Board / Comunicazioni reparto
export async function apiGetBoardFeed({ status, query, limit } = {}) {
  const qs = new URLSearchParams();
  if (status) qs.set('status', status);
  if (query) qs.set('query', query);
  if (limit) qs.set('limit', String(limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return fetchApi(`/api/board/feed${suffix}`);
}

export async function apiGetBoardPost(postId) {
  return fetchApi(`/api/board/posts/${encodeURIComponent(postId)}`);
}

export async function apiCreateBoardPost(post) {
  return fetchApi('/api/board/posts', {
    method: 'POST',
    body: JSON.stringify(post),
  });
}

export async function apiUploadBoardAttachment(file) {
  const form = new FormData();
  form.append('file', file);
  const auth = getAuthHeaders().Authorization;
  const headers = auth ? { Authorization: auth } : {};
  const r = await fetch(`${API_BASE}/api/board/uploads`, {
    method: 'POST',
    headers,
    body: form,
  });
  if (!r.ok) {
    let detail = r.statusText;
    try {
      const body = await r.json();
      if (body.detail) detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    } catch (_) {}
    throw new Error(`API ${r.status}: ${detail}`);
  }
  return r.json();
}

export async function apiUpdateBoardPost(postId, updates) {
  return fetchApi(`/api/board/posts/${encodeURIComponent(postId)}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function apiSubmitBoardPost(postId, note = '') {
  return fetchApi(`/api/board/posts/${encodeURIComponent(postId)}/submit`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export async function apiApproveBoardPost(postId, note = '') {
  return fetchApi(`/api/board/posts/${encodeURIComponent(postId)}/approve`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export async function apiRejectBoardPost(postId, note = '') {
  return fetchApi(`/api/board/posts/${encodeURIComponent(postId)}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export async function apiPublishBoardPost(postId, note = '') {
  return fetchApi(`/api/board/posts/${encodeURIComponent(postId)}/publish`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export async function apiArchiveBoardPost(postId, note = '') {
  return fetchApi(`/api/board/posts/${encodeURIComponent(postId)}/archive`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export async function apiDeleteBoardPost(postId) {
  return fetchApi(`/api/board/posts/${encodeURIComponent(postId)}`, {
    method: 'DELETE',
  });
}

export async function apiMarkBoardRead(postId, acknowledged = false, sourceChannel = 'in_app') {
  return fetchApi(`/api/board/posts/${encodeURIComponent(postId)}/read`, {
    method: 'POST',
    body: JSON.stringify({ acknowledged, sourceChannel }),
  });
}

export async function apiGetBoardDeliveries(postId, limit = 200) {
  return fetchApi(`/api/board/posts/${encodeURIComponent(postId)}/deliveries?limit=${encodeURIComponent(limit)}`);
}

export async function apiGetBoardPostReadStatus(postId) {
  return fetchApi(`/api/board/posts/${encodeURIComponent(postId)}/read-status`);
}

export async function apiGetBoardModerationQueue(limit = 50) {
  return fetchApi(`/api/board/moderation/queue?limit=${encodeURIComponent(limit)}`);
}

export async function apiGetBoardTargetUsers() {
  return fetchApi('/api/board/target-users');
}

export async function apiGetShiftChangeOptions() {
  return fetchApi('/api/board/shift-change/options');
}

export async function apiCreateShiftChangeRequest(payload) {
  return fetchApi('/api/board/shift-change-requests', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function apiListShiftChangeRequests(status = null) {
  const qs = new URLSearchParams();
  if (status) qs.set('status', status);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return fetchApi(`/api/board/shift-change-requests${suffix}`);
}

export async function apiApproveShiftChangeRequest(requestId, note = '') {
  return fetchApi(`/api/board/shift-change-requests/${encodeURIComponent(requestId)}/approve`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export async function apiRejectShiftChangeRequest(requestId, note = '') {
  return fetchApi(`/api/board/shift-change-requests/${encodeURIComponent(requestId)}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export async function apiPeerApproveShiftChangeRequest(requestId, note = '') {
  return fetchApi(`/api/board/shift-change-requests/${encodeURIComponent(requestId)}/peer-approve`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export async function apiPeerRejectShiftChangeRequest(requestId, note = '') {
  return fetchApi(`/api/board/shift-change-requests/${encodeURIComponent(requestId)}/peer-reject`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export async function apiDismissShiftChangeRequest(requestId) {
  return fetchApi(`/api/board/shift-change-requests/${encodeURIComponent(requestId)}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function apiGetBoardPreferences() {
  return fetchApi('/api/board/preferences');
}

export async function apiSetBoardPreferences(preferences) {
  return fetchApi('/api/board/preferences', {
    method: 'PUT',
    body: JSON.stringify(preferences),
  });
}

export async function apiGetBoardAnalyticsSummary() {
  return fetchApi('/api/board/analytics/summary');
}

export async function apiDownloadBoardCalendarIcs(dateFrom, dateTo) {
  const qs = new URLSearchParams();
  qs.set('date_from', dateFrom);
  qs.set('date_to', dateTo);
  const r = await fetch(`${API_BASE}/api/board/calendar.ics?${qs.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!r.ok) {
    let detail = r.statusText;
    try {
      const body = await r.json();
      if (body.detail) detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    } catch (_) {}
    throw new Error(`API ${r.status}: ${detail}`);
  }
  return r.blob();
}

export async function apiDownloadBoardPostCalendarIcs(postId) {
  const r = await fetch(`${API_BASE}/api/board/posts/${encodeURIComponent(postId)}/calendar.ics`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!r.ok) {
    let detail = r.statusText;
    try {
      const body = await r.json();
      if (body.detail) detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    } catch (_) {}
    throw new Error(`API ${r.status}: ${detail}`);
  }
  const contentDisposition = r.headers.get('Content-Disposition') || '';
  const match = /filename="?([^"]+)"?/i.exec(contentDisposition);
  return { blob: await r.blob(), fileName: match?.[1] || `board-event-${String(postId || '').slice(0, 8)}.ics` };
}

export async function apiDownloadBoardAttachmentByUrl(urlPath) {
  const rel = String(urlPath || '').trim();
  if (!rel) throw new Error('URL allegato non valida');
  let path = rel.startsWith('/') ? rel : `/${rel}`;
  try {
    const parsed = new URL(rel, window.location.origin);
    path = parsed.pathname || path;
  } catch (_) {}
  const r = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!r.ok) {
    let detail = r.statusText;
    try {
      const body = await r.json();
      if (body.detail) detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    } catch (_) {}
    throw new Error(`API ${r.status}: ${detail}`);
  }
  const contentDisposition = r.headers.get('Content-Disposition') || '';
  const match = /filename="?([^"]+)"?/i.exec(contentDisposition);
  return {
    blob: await r.blob(),
    fileName: match?.[1] || 'allegato',
    contentType: r.headers.get('Content-Type') || '',
  };
}

/** Chiave pubblica VAPID per Web Push (senza Bearer). */
export async function apiGetPushVapidPublicKey() {
  try {
    const r = await fetch(`${API_BASE}/api/push/vapid-public-key`, { method: 'GET' });
    if (!r.ok) return { enabled: false, publicKey: null };
    return await r.json();
  } catch {
    return { enabled: false, publicKey: null };
  }
}

export async function apiPushSubscribe(subscriptionJson) {
  const body = {
    endpoint: subscriptionJson.endpoint,
    keys: subscriptionJson.keys,
  };
  return fetchApi('/api/push/subscribe', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function apiPushUnsubscribe(endpoint) {
  return fetchApi('/api/push/unsubscribe', {
    method: 'POST',
    body: JSON.stringify({ endpoint: String(endpoint || '') }),
  });
}

export async function apiPushTest(payload) {
  return fetchApi('/api/push/test', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  });
}
